// Fields: the details the team needs, per case type.

import React, { useState } from 'react';
import { I } from '../../../common/v5/config';
import {
	addField,
	ATTACHMENTS_DEFAULT,
	cleanKey,
	FIELD_TYPE_NAMES,
	FIELD_TYPES,
	makeKey,
	moveItem,
	problemFor,
	removeField,
	setText,
	textIn
} from './definition';
import { Check, Field, IconButton, LangText, MoveRemove, Section, Switch } from './ui';

function BuiltinRow({ title, field, lang, main, canBeOptional, onChange, problems, path }) {
	return (
		<div className="aif-builtin">
			<LangText label={title + ": " + I("label")} value={field.label} lang={lang} mainLang={main}
				onChange={label => onChange(Object.assign({}, field, { label }))} error={problemFor(problems, path + ".label")} />
			<LangText label={I("Placeholder")} value={field.placeholder} lang={lang} mainLang={main}
				onChange={placeholder => onChange(Object.assign({}, field, { placeholder }))} />
			<div style={{ paddingBottom: 7 }}>
				<Check checked={field.required} disabled={!canBeOptional} label={I("Required")}
					onChange={required => onChange(Object.assign({}, field, { required }))} />
			</div>
		</div>
	);
}

function AttachmentsRow({ value, lang, main, legacyField, onChange }) {
	return (
		<div className="aif-section-body" style={{ borderTop: "1px solid #eef1f4", paddingTop: 12 }}>
			<Switch checked={value.enabled} onChange={enabled => onChange(Object.assign({}, value, { enabled }))}
				label={I("Attachments")}
				description={value.enabled ? I("Customers can attach files.") : I("Off: this form has no file upload.")} />
			{value.enabled && (
				<div className="aif-builtin">
					<LangText label={I("Attachments: label")} value={value.label} lang={lang} mainLang={main}
						onChange={label => onChange(Object.assign({}, value, { label }))} hint={I("Empty: “Bilagor” / “Attachments”.")} />
					<LangText label={I("Help text")} value={value.help} lang={lang} mainLang={main}
						onChange={help => onChange(Object.assign({}, value, { help }))} />
					<div style={{ paddingBottom: 7 }}>
						<Check checked={value.required} label={I("Required")}
							onChange={required => onChange(Object.assign({}, value, { required }))} />
					</div>
				</div>
			)}
			{value.enabled && legacyField && (
				<p className="aif-small" style={{ color: "#946200" }}>
					{I("This form also has an older “Attachments” field below. Its label and Required setting are used until you remove it.")}
				</p>
			)}
		</div>
	);
}

function Options({ field, lang, main, problem, onChange }) {
	return (
		<div>
			<label className="aif-lbl">{I("Options")}</label>
			{field.options.map((option, oi) => (
				<div key={oi} style={{ display: "flex", gap: 6, alignItems: "center", marginTop: oi ? 6 : 0 }}>
					<input type="text" aria-label={I("Option") + " " + (oi + 1)}
						value={option.label[lang] || ""}
						placeholder={textIn(option.label, main, main)}
						onChange={e => {
							const label = setText(option.label, lang, e.target.value);
							const others = field.options.filter((_, j) => j !== oi).map(o => o.value);
							const value = lang === main && e.target.value ? makeKey(e.target.value, others) : option.value;
							onChange(field.options.map((o, j) => (j === oi ? { value, label } : o)));
						}} />
					<IconButton label={I("Remove option")} icon="fa-times" disabled={field.options.length <= 1}
						onClick={() => onChange(field.options.filter((_, j) => j !== oi))} />
				</div>
			))}
			{problem && <div className="aif-error">{problem}</div>}
			<button type="button" className="aif-link" style={{ marginTop: 6 }}
				onClick={() => {
					const n = field.options.length + 1;
					const value = makeKey("option " + n, field.options.map(o => o.value));
					onChange(field.options.concat([{ value, label: { [main]: I("Option") + " " + n } }]));
				}}>
				+ {I("Add option")}
			</button>
		</div>
	);
}

export default function FieldsTab({ defn, lang, problems, onChange }) {
	const [newType, setNewType] = useState("text");
	const main = defn.default_language;

	const update = (index, patch) => {
		const fields = defn.fields.map((f, i) => (i === index ? Object.assign({}, f, patch) : f));
		onChange(Object.assign({}, defn, { fields }));
	};

	// Only one field can be the subject.
	const setSubject = (index, on) => {
		const fields = defn.fields.map((f, i) => Object.assign({}, f, { maps_to: i === index && on ? "case.subject" : "form" }));
		onChange(Object.assign({}, defn, { fields }));
	};

	const set = (key) => (value) => onChange(Object.assign({}, defn, { [key]: value }));

	return (
		<div className="aif-stack">
			<Section title={I("Always on the form")} hint={I("The description is what the AI answers and what your team reads first.")}>
				<BuiltinRow title={I("Description")} field={defn.description} lang={lang} main={main} canBeOptional={false}
					onChange={set("description")} problems={problems} path="description" />
				<BuiltinRow title={I("Name")} field={defn.name} lang={lang} main={main} canBeOptional
					onChange={set("name")} problems={problems} path="name" />
				<BuiltinRow title={I("Email")} field={defn.email} lang={lang} main={main} canBeOptional
					onChange={set("email")} problems={problems} path="email" />
				<AttachmentsRow value={defn.attachments || ATTACHMENTS_DEFAULT} lang={lang} main={main}
					legacyField={defn.fields.some(f => f.type === "file")} onChange={set("attachments")} />
			</Section>

			{defn.fields.map((field, index) => {
				const path = "fields[" + index + "]";
				const allTypes = field.case_types.length === 0;
				const shownFor = allTypes ? I("every case type")
					: (field.case_types.length === 1 ? I("1 case type") : I("{n} case types").replace("{n}", field.case_types.length));
				return (
					<Section
						key={index}
						title={textIn(field.label, lang, main) || field.key}
						hint={(FIELD_TYPE_NAMES[field.type] || field.type) + " · " + shownFor}
						action={
							<MoveRemove index={index} count={defn.fields.length}
								onMove={d => onChange(Object.assign({}, defn, { fields: moveItem(defn.fields, index, d) }))}
								removeLabel={I("Remove field")}
								onRemove={() => onChange(removeField(defn, field.key))} />
						}
					>
						<div className="aif-grid2">
							<LangText label={I("Label")} value={field.label} lang={lang} mainLang={main}
								onChange={label => update(index, { label })} error={problemFor(problems, path + ".label")} />
							<Field label={I("Type")} error={problemFor(problems, path + ".type")}>
								{id => (
									<select id={id} value={field.type} onChange={e => {
										const type = e.target.value;
										const options = type === "select" && field.options.length === 0
											? [{ value: "option_1", label: { [main]: I("Option") + " 1" } }]
											: field.options;
										update(index, { type, options, maps_to: type === "text" ? field.maps_to : "form" });
									}}>
										{FIELD_TYPES.filter(t => t !== "file" || field.type === "file").map(t =>
											<option key={t} value={t}>{FIELD_TYPE_NAMES[t]}</option>)}
									</select>
								)}
							</Field>
							{field.type !== "checkbox" && field.type !== "file" && (
								<LangText label={I("Placeholder")} value={field.placeholder} lang={lang} mainLang={main}
									onChange={placeholder => update(index, { placeholder })} />
							)}
							<LangText label={I("Help text")} value={field.help} lang={lang} mainLang={main}
								onChange={help => update(index, { help })} />
						</div>

						{field.type === "select" && (
							<Options field={field} lang={lang} main={main} problem={problemFor(problems, path + ".options")}
								onChange={options => update(index, { options })} />
						)}

						<fieldset style={{ border: 0, padding: 0, margin: 0 }}>
							<label className="aif-lbl">{I("Shown for")}</label>
							<div className="aif-row" style={{ gap: "6px 16px" }}>
								<Check checked={allTypes} label={I("Every case type")}
									onChange={() => update(index, { case_types: allTypes ? [defn.case_types[0].key] : [] })} />
								{!allTypes && defn.case_types.map(ct => (
									<Check key={ct.key} checked={field.case_types.indexOf(ct.key) >= 0}
										label={textIn(ct.label, lang, main) || ct.key}
										onChange={on => {
											const next = on ? field.case_types.concat([ct.key]) : field.case_types.filter(k => k !== ct.key);
											update(index, { case_types: next.length ? next : field.case_types });
										}} />
								))}
							</div>
							{problemFor(problems, path + ".case_types") && <div className="aif-error">{problemFor(problems, path + ".case_types")}</div>}
						</fieldset>

						<div className="aif-row">
							<Switch checked={field.required} onChange={required => update(index, { required })} label={I("Required")} />
							{field.type === "text" && (
								<Switch checked={field.maps_to === "case.subject"} onChange={on => setSubject(index, on)}
									label={I("Use as the errand subject")} />
							)}
							<Field label={I("Key")} error={problemFor(problems, path + ".key")}>
								{id => (
									<input id={id} type="text" className="aif-mono aif-key" value={field.key}
										onChange={e => update(index, { key: cleanKey(e.target.value) })} />
								)}
							</Field>
						</div>
					</Section>
				);
			})}

			<div className="aif-addfield">
				<label htmlFor="aif-new-field-type" className="aif-muted" style={{ margin: 0 }}>{I("Add a field:")}</label>
				<select id="aif-new-field-type" value={newType} onChange={e => setNewType(e.target.value)}>
					{/* Attachments live under "Always on the form". */}
					{FIELD_TYPES.filter(t => t !== "file").map(t => <option key={t} value={t}>{FIELD_TYPE_NAMES[t]}</option>)}
				</select>
				<button type="button" className="aif-btn" disabled={defn.fields.length >= 30}
					onClick={() => onChange(addField(defn, newType))}>
					<i className="fa fa-plus" aria-hidden="true" /> {I("Add")}
				</button>
			</div>
		</div>
	);
}
